import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { authClient } from "lib/auth-client";
import { ENVS } from "config/envs";
import {
  getBearerSessionToken,
  resolveAuthBearerToken,
  setBearerSessionToken,
} from "lib/better-auth-session";
import { setRefreshToken, setToken } from "store/reducers/auth";
import { store } from "store/actions/store";
import type { AppState } from "store/reducers/Reducer";
import { fetchHealth } from "hooks/useHealthCheck/useHealthCheck";
import { isMaintenanceHealthResult } from "hooks/auth/useMaintenanceStatus";
import { captureAuthSession, isAuthSessionUnchanged } from "lib/auth-session-ownership";

/** Bound session restore so web-scoped maintenance cannot hang on get-session forever. */
const BETTER_AUTH_SESSION_BOOTSTRAP_TIMEOUT_MS = 4000;

function shouldBootstrapBetterAuthSession(): boolean {
  if (!ENVS.betterAuthEnabled) {
    return false;
  }

  const state = store.getState() as AppState;
  const token = resolveAuthBearerToken(state.auth.token) || getBearerSessionToken();
  return Boolean(token || state.auth.loggedIn);
}

async function getSessionWithTimeout() {
  return Promise.race([
    authClient.getSession(),
    new Promise<never>((_, reject) => {
      window.setTimeout(() => {
        reject(new Error("Better Auth session bootstrap timed out"));
      }, BETTER_AUTH_SESSION_BOOTSTRAP_TIMEOUT_MS);
    }),
  ]);
}

/**
 * Restores the Better Auth bearer token in the background.
 * Does not gate the React tree — ErrorHandler must mount so web-scoped
 * maintenance can show instead of an indefinite "Restoring session…" spinner.
 */
export function useBetterAuthSessionBootstrap(): { isBootstrapping: boolean } {
  const dispatch = useDispatch();
  const [isBootstrapping, setIsBootstrapping] = useState(shouldBootstrapBetterAuthSession);

  useEffect(() => {
    if (!ENVS.betterAuthEnabled) {
      return;
    }

    if (!shouldBootstrapBetterAuthSession()) {
      setIsBootstrapping(false);
      return;
    }

    let cancelled = false;
    const owner = captureAuthSession();

    void (async () => {
      try {
        const health = await fetchHealth();
        if (cancelled || !isAuthSessionUnchanged(owner)) {
          return;
        }
        if (isMaintenanceHealthResult(health)) {
          return;
        }

        const sessionResult = await getSessionWithTimeout();
        if (cancelled || !isAuthSessionUnchanged(owner)) {
          return;
        }

        const sessionToken = sessionResult.data?.session?.token;
        if (typeof sessionToken === "string" && sessionToken.length > 0) {
          setBearerSessionToken(sessionToken);
          dispatch(setToken(sessionToken));
          dispatch(setRefreshToken(sessionToken));
        }
      } catch {
        // Keep persisted auth state; API 401 handlers will logout if the session is invalid.
      } finally {
        if (!cancelled) {
          setIsBootstrapping(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      // StrictMode remounts must not leave the previous instance stuck bootstrapping.
      setIsBootstrapping(false);
    };
  }, [dispatch]);

  return { isBootstrapping };
}
